import { Trans, t } from '@lingui/macro';
import styled from '@emotion/styled';
import { AuthLink } from '../AuthLink';
import { useSupplierSignUp } from '../../api/useSupplierSignUp';
import { EmailChip, ProgressDots, ProgressDot } from './SharedComponents';
import { Text } from 'components/text/Text';
import { BaseButton } from 'components/button/Button';

type LoginExistingUserProps = {
  onSelectCompany: (id: number, name: string) => void;
  onCreateNewAccount: () => void;
};

export const LoginExistingUser = ({
  onSelectCompany,
  onCreateNewAccount,
}: LoginExistingUserProps) => {
  const { signupResponse, clearSignupResponse } = useSupplierSignUp();

  return (
    <LeftPanelContainer>
      <EmailChip email={signupResponse?.email} />
      <ProgressDots top={8}>
        <ProgressDot active />
        <ProgressDot active={false} />
      </ProgressDots>
      <Text variant="headlineMd" weight="semiBold" bottom={8} top={8}>
        <Trans>Welcome back</Trans>
      </Text>
      <Text variant="bodyMd" bottom={24} color="secondary">
        <Trans>
          This email is linked to existing supplier accounts. Log in below, or create a new one.
        </Trans>
      </Text>
      {signupResponse?.userAssociatedSupplierAccounts?.length ? (
        <LoginExistingList>
          {signupResponse.userAssociatedSupplierAccounts.map(account => (
            <div key={account.supplierId}>
              <BaseButton
                type="button"
                onClick={() => onSelectCompany(account.supplierId, account.supplierName)}
              >
                <Text variant="bodySm" color="link" weight="semiBold">
                  Log in to {account.supplierName}
                </Text>
              </BaseButton>
            </div>
          ))}
        </LoginExistingList>
      ) : null}
      <OrDivider>
        <OrLine />
        <Text variant="bodyXs" color="secondary">
          <Trans>or</Trans>
        </Text>
        <OrLine />
      </OrDivider>
      <CreateAccountLinkWrapper>
        <AuthLink
          questionText=""
          linkText={t`Create a new supplier account`}
          onClick={onCreateNewAccount}
        />
      </CreateAccountLinkWrapper>
      <AuthLink
        linkTo="/supplier/signup"
        questionText={t`Not what you were looking for?`}
        linkText={t`Click here to cancel`}
        onClick={clearSignupResponse}
        light
      />
    </LeftPanelContainer>
  );
};

const LeftPanelContainer = styled.div`
  margin: ${({ theme }) => theme.s.lg};
  margin-bottom: ${({ theme }) => theme.s.xs};
  display: inline-flex;
  flex-direction: column;
  width: calc(100% - ${({ theme }) => theme.s.xxl});
  border-radius: ${({ theme }) => theme.s.sm};
`;

const CreateAccountLinkWrapper = styled.div`
  width: 100%;
  & > div {
    justify-content: flex-start;
  }
`;

const LoginExistingList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.s.xxxs};
  margin-bottom: ${({ theme }) => theme.s.md};
  button {
    max-width: ${p => `calc(100% - ${p.theme.s.md} - ${p.theme.s.md})`};
    span {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      display: block;
      max-width: 100%;
    }
    :hover {
      span {
        text-decoration: underline;
      }
    }
    :focus {
      span {
        outline: 1px solid ${p => p.theme.palette.border.brand};
      }
    }
  }
`;

const OrDivider = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.s.sm};
  margin: ${({ theme }) => theme.s.xs} 0;
`;

const OrLine = styled.span`
  flex: 1;
  height: 1px;
  background-color: ${p => p.theme.palette.border.primary};
`;
